<template>
    <div class="app-popup" :class="type">
        <popup-animations v-if="animation" :animation="animation" :element="element" />
        <div v-if="hasHeader" class="app-popup-header">
            <h3>{{ title }}</h3>
            <div v-if="closeButton" class="close-btn" @click="closePopup"><s3-close-svg /></div>
        </div>
        <div class="app-popup-content">
            <div v-html="content"></div>
            <popup-elements v-if="element" :element="element" @close="closePopup" />
            <app-input 
                v-if="type === 'text-confirm-popup'" v-model="inputValue"
                class="large-input"
                :label="`Please type '${ confirmText }'`"
                :is-valid="isValid"
                error="The text you entered does not match the required text" />
        </div>
        <div v-if="actions" class="app-popup-actions">
            <app-button v-if="actions.cancel" class="transparent-btn" :on-click="closePopup" data-locator="app-popup-action-cancel">{{ actions.cancel.label }}</app-button>
            <app-button v-if="actions.secondary" class="secondary-btn" :on-click="handleSecondary" data-locator="app-popup-action-secondary">
                {{ actions.secondary.label }}
            </app-button>
            <app-button v-if="actions.tertiary" class="tertiary-btn" :on-click="handleTertiary" data-locator="app-popup-action-tertiary">
                {{ actions.tertiary.label }}
            </app-button>
            <app-button v-if="actions.confirm" :class="[ actions.confirm.type ]" :on-click="confirm" :disabled="!isValid && type == 'text-confirm-popup'" data-locator="app-popup-action-confirm">
                {{ actions.confirm.label }}
                <div v-if="actions.confirm.type" class="icon-container">
                    <leave-svg v-if="actions.confirm.type === 'leave'" />
                    <trash-can-svg v-else-if="actions.confirm.type === 'remove'" />
                </div>
        </app-button>
        </div>
    </div>
</template>

<script>
import LeaveSvg from '@/components/svg/leave-svg';
import TrashCanSvg from '@/components/svg/trash-can-svg';
import AppButton from '@/components/common/app-button';
import AppInput from '@/components/common/app-input';
import PopupAnimations from '@/components/popups/popup-animations';
import popupElements from '@/components/popups/popup-elements/popup-elements';
import S3CloseSvg from '@/components/svg/grid/s3-close-svg';

export default {
    name: 'app-popup',

    components: {
        'leave-svg': LeaveSvg,
        'trash-can-svg': TrashCanSvg,
        'app-button': AppButton,
        'app-input': AppInput,
        's3-close-svg': S3CloseSvg,
        'popup-animations': PopupAnimations,
        'popup-elements': popupElements,
    },

    props: {
        element:{
            type: Object,
            default: null,
        },

        content: {
            type: String
        },

        type: {
            type: String
        },

        title: {
            type: String
        },

        actions: {
            type: Object,
            default: null
        },

        animation: {
            type: String,
            default: null
        },

        args: {
            type: Array
        },

        confirmText: {
            type: String,
            default: null
        },

        hasHeader:{
            type: Boolean,
            default: true
        },

        closeButton: {
            type:Boolean,
            default:true,
        },
    },

    data() {
        return {
            inputValue: ''
        }
    },

    computed: {
        isValid() {
            return this.inputValue === this.confirmText;
        }
    },

    methods: {
        closePopup() {
            this.$emit('close');
        },

        confirm() {
            if (this.args && this.args.length > 0) {
                this.actions.confirm.action(...this.args);
            } else {
                this.actions.confirm.action();
            }

            this.closePopup();
        },

        handleSecondary() {
            this.actions.secondary.action();
            this.closePopup();
        },

        handleTertiary() {
            this.actions.tertiary.action();
            this.closePopup();
        }
    }
}
</script>

<style>
.app-popup .app-popup-header {
    text-align: center;
    padding: 20px 30px;
    position: relative;
}

.app-popup .app-popup-header::after {
    position: absolute;
    content: '';
    width: 10vw;
}

.app-popup.alert-popup .app-popup-header::after,
.app-popup.text-confirm-popup .app-popup-header::after {
    max-width: 156px;
    height: 234px;
    top: -75px;
    left: -70px;
    background: transparent url('../../assets/images/SVG/alert-confirmation.svg') no-repeat top center;
    background-size: contain;
}

.app-popup.success-popup .app-popup-header::after {
    max-width: 190px;
    height: 175px;
    top: -65px;
    left: -70px;
    background: transparent url('../../assets/images/SVG/success-confirmation.svg') no-repeat top center;
    background-size: contain;
}

.app-popup.error-popup .app-popup-header::after {
    max-width: 185px;
    height: 185px;
    top: -60px;
    left: -70px;
    background: transparent url('../../assets/images/SVG/error-confirmation.svg') no-repeat top center;
    background-size: contain;
}

.app-popup .app-popup-header h3 {
    margin-bottom: 0;
    display: inline-block;
    max-width: 90%;
    line-height: 1.5em;
    font-size: 30px;
}

.app-popup.success .app-popup-header h3 {
    color: #2ADA2B;
}

.app-popup.error .app-popup-header h3 {
    color: #F30070;
}

.app-popup.alternate-success .app-popup-header h3 {
    color: #18AEFF;
}

.app-popup .app-popup-header .close-btn {
    cursor: pointer;
    position: absolute;
    right: 20px;
    top: 20px;
}

.app-popup .app-popup-header .close-btn svg {
    position: relative;
    z-index: 10;
}

.app-popup .app-popup-header .close-btn::before {
    content: '';
    display: inline-block;
    height: 1px;
    width: 1px;
    border-radius: 50%;
    background: #F5D5E7;
    position: absolute;
    top: 50%;
    margin-top: -3px;
    left: 50%;
    margin-left: -1px;
    z-index: 0;
    transition: all .2s ease-in-out;
}

.app-popup .app-popup-header .close-btn:hover::before {
    transform: scale(50);
}

.app-popup .app-popup-content {
    color: #382153;
    margin: 0 30px;
    padding: 20px 30px;
    font: normal 22px/37px 'Nunito';
}

.app-popup .app-popup-actions {
    border-top: 2px solid #E9E9EF;
    display: flex;
    justify-content: center;
    margin: 0 30px;
    padding: 28px 17px;
}

#modals-container .v--modal-box {
    overflow: initial;
}

.app-popup .app-popup-actions .btn {
    margin-left: 15px;
    min-width: 151px;
    font-size: 16px;
}

.app-popup .app-popup-actions .btn.transparent-btn {
    border: 2px solid #CAC6CB;
    background: transparent;
    color: #382153;
}

.app-popup .app-popup-actions .btn.transparent-btn:hover {
    border: 2px solid var(--color-border-button-alternate);
    background: var(--color-fill-accent-ghost);
    color: var(--main-void-base);
}

.app-popup .app-popup-actions .btn.secondary-btn {
    border: 2px solid #18aeff;
    background-color: transparent;
    color: #18aeff;
    -webkit-transition: none;
    transition: none;
}

.app-popup .app-popup-actions .btn.secondary-btn:hover {
    border: 2px solid #18aeff;
    background-color: #18aeff;
    color: #ffffff;
}

.app-popup .app-popup-actions .btn.tertiary-btn{
    border: 1px solid #CAC6CB;
    background: transparent;
    color: #382153;
}

.app-popup .app-popup-actions .btn.tertiary-btn:hover {
    border: 2px solid #CAC6CB;
    background: #DCDCDC;
}

.app-popup .app-popup-actions .btn.remove {
    background: #F30070;
}

.app-popup .app-popup-actions .btn.remove:hover {
    background: #D10E68
}

.app-popup .app-popup-actions .btn.removeNotIcon {
    background: #f30070;
}

.app-popup .app-popup-actions .btn.removeNotIcon:hover {
    background: #d10e68;
}

.app-popup .app-popup-actions .btn svg {
    margin-left: 10px;
}

.app-popup .app-popup-actions .btn .icon-container {
    height: 16px;
}

.app-popup .app-popup-actions .btn .trash-can-svg {
    height: 16px;
}

.app-popup .app-popup-actions .btn .trash-can-svg path,
.app-popup .app-popup-actions .btn .trash-can-svg rect {
    fill: #FFFFFF
}

.app-popup.role-popup .app-popup-content{
    border: none;
}

</style>
